// portal/src/components/ChatDock.tsx
// ────────────────────────────────────────────────────────────────────
// Right-hand chat dock for the Corrosion Agent. Client-held transcript
// (multi-turn); each send posts the last N turns + the workspace focus.
// Renders the deterministic metrics card SEPARATELY from the LLM answer
// (numbers come from `metrics`, never parsed out of the prose).
// ────────────────────────────────────────────────────────────────────
import { Fragment, useEffect, useRef, useState } from "react";
import { Bot, Send, Lock, Hexagon, X } from "lucide-react";
import { agentChat, type ChatMessage, type ChatMetrics } from "@/lib/agentApi";
import { apiClient, errMsg, getCachedUser, STAFF_ROLES } from "@/lib/api";
import { useWorkspaceFocus } from "./WorkspaceContext";
import { useEntitlements, PremiumBadge } from "./Gated";
import { useDock, TrayToggle, ComposerTray } from "./dock";

const HISTORY_TURNS = 6;            // 🔧 turns sent to the server
// 🔧 M4: remaining life is "preliminary" until representative_mm is confirmed
// to be the MINIMUM reading. Flip to false once that data-semantics check passes.
const REMAINING_LIFE_PRELIMINARY = true;

interface Turn {
  role: "user" | "assistant";
  content: string;
  metrics?: ChatMetrics;
  at: number;                         // epoch ms the turn was added
}

function fmt(n: unknown, digits = 2): string {
  return typeof n === "number" ? n.toFixed(digits) : "—";
}

// Sender + date/time stamp for a message, e.g. "01 Jul, 14:32" (locale-aware, 24h).
function fmtWhen(at: number): string {
  return new Date(at).toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function MetricsCard({ m }: { m: Record<string, unknown> }): JSX.Element {
  const quality = String(m.data_quality ?? "");
  const insufficient = quality === "insufficient";
  return (
    <div className="mb-2 border border-hairline bg-paper-2 p-2 font-mono text-[10px] text-ink-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-accent-deep uppercase tracking-wider">
          {String(m.cml_no ?? m.governing_cml_no ?? "metrics")}
        </span>
        {quality && (
          <span className={
            quality === "measured" ? "text-status-ok" : "text-status-warn"
          }>{quality}</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {/* With insufficient data the rate/RL are not computable — show that, not
            a fake "0.0000 mm/yr" / "0.0 yr" that reads as a real measurement. */}
        <span>Gov. CR</span>
        <span className="text-ink-1">{insufficient ? "—" : `${fmt(m.governing_rate_mm_yr, 4)} mm/yr`}</span>
        <span>Remaining life</span>
        <span className="text-ink-1">
          {insufficient ? "insufficient data"
            : m.no_measurable_corrosion ? "no measurable loss"
            : `${fmt(m.remaining_life_years, 1)} yr`}
          {REMAINING_LIFE_PRELIMINARY && !insufficient && !m.no_measurable_corrosion && (
            <span className="ml-1 text-status-warn">· preliminary</span>
          )}
        </span>
      </div>
    </div>
  );
}

// Retrieval-agent result: a compact data table + roll-up, rendered from the
// deterministic `metrics` (rows/summary), never parsed from the prose. Every row
// is grounded in the tenant's own records.
function RetrievalCard({ m }: { m: Record<string, unknown> }): JSX.Element {
  const rows = Array.isArray(m.rows) ? (m.rows as Record<string, unknown>[]) : [];
  const summary = m.summary && typeof m.summary === "object"
    ? (m.summary as Record<string, unknown>) : null;
  const tool = String(m.tool ?? "data").replace(/_/g, " ");
  const cols = rows.length ? Object.keys(rows[0]) : [];
  const shown = rows.slice(0, 8);
  return (
    <div className="mb-2 border border-hairline bg-paper-2 p-2 font-mono text-[10px] text-ink-2">
      <div className="text-accent-deep uppercase tracking-wider mb-1">{tool}</div>
      {summary && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mb-1.5">
          {Object.entries(summary).map(([k, v]) => (
            <Fragment key={k}>
              <span>{k}</span>
              <span className="text-ink-1 tabular-nums">{v == null ? "—" : String(v)}</span>
            </Fragment>
          ))}
        </div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>{cols.map((c) => (
                <th key={c} className="text-left pr-2 pb-0.5 font-normal text-ink-4 whitespace-nowrap">
                  {c.replace(/_/g, " ")}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={i} className="border-t border-hairline">
                  {cols.map((c) => (
                    <td key={c} className="pr-2 py-0.5 text-ink-1 tabular-nums whitespace-nowrap">
                      {r[c] == null ? "—" : String(r[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > shown.length && (
            <div className="text-ink-4 mt-0.5">+{rows.length - shown.length} more</div>
          )}
        </div>
      )}
      <div className="text-ink-4 mt-1">
        {rows.length} row{rows.length === 1 ? "" : "s"} · from your inspection records
      </div>
    </div>
  );
}

// Inline "MODS is composing" indicator — honest signal for an actual
// in-flight request, styled as the same left-aligned answer-bubble shape
// (radii, border, background) a real reply uses, so a live request reads as
// part of the conversation instead of a stray status string.
function TypingIndicator(): JSX.Element {
  return (
    <div className="self-start" style={{ maxWidth: "82%" }} data-testid="mods-typing">
      <div className="font-mono px-2.5 py-1.5 inline-flex items-center gap-1" style={{
        background: "var(--paper)", border: "1px solid var(--hairline)",
        borderRadius: "10px 10px 10px 2px",
      }}>
        {[0, 1, 2].map((i) => (
          <span key={i} className="inline-block rounded-full animate-pulse"
                style={{ width: 4, height: 4, background: "var(--ink-4)", animationDelay: `${i * 160}ms` }} />
        ))}
      </div>
    </div>
  );
}

// ModsBody — MODS's inner content (disclosure banner, transcript, starter
// chips, composer). Renders WITHOUT its own fixed positioning or open/close
// toggle — those are now the DockWindow/DockBar's job (see ModsDockMounter
// below, and docs/superpowers/specs/2026-07-21-unified-dock.md Phase 1b).
// A component reference kept stable across renders (module-level function,
// no dynamic identity) so registering it as a dock window body never causes
// a remount.
export function ModsBody(): JSX.Element {
  // Same STAFF_ROLES source AppShell/TeamChatDock use to gate nav/mounts — no
  // second notion of "staff" invented here. Customers get the safe, records-
  // only variant: no concern-ranking chip (worst_cmls, backend-denied for
  // them now) and an explicit "data, not a verdict" caveat.
  const cachedUser = getCachedUser();
  const isStaff = !!cachedUser && (STAFF_ROLES as readonly string[]).includes(cachedUser.role);
  const { focus, setFocus } = useWorkspaceFocus();
  const { data: ent } = useEntitlements();
  const aiEntitled = !!ent?.features.includes("ai_agent");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  // The quick-question chips live behind the same "+" as the chat's attach
  // tools (user: "chat ของ MODS ก็แบบเดียวกัน") — press + and they stretch out
  // inline in the composer row; picking one dispatches and collapses back.
  const [chipsOpen, setChipsOpen] = useState(false);
  // ⬡ asset-focus picker — the tray's one "attach"-like tool that means
  // something in MODS (files/notes are conversation tools; see the chat).
  // Picking a REAL asset sets the workspace focus -> the focus-dependent
  // chips (readings / thickness trend) unlock. Options load once on open.
  const [assetPickOpen, setAssetPickOpen] = useState(false);
  const [assetQuery, setAssetQuery] = useState("");
  const [assetOptions, setAssetOptions] = useState<{ id: number; tag: string }[] | null>(null);
  const [assetErr, setAssetErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view, like a normal chat.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, busy]);

  function openAssetPick(): void {
    setAssetPickOpen((v) => !v); setChipsOpen(false); setAssetErr(false);
    if (assetOptions === null) {
      apiClient.assets()
        .then((rows) => setAssetOptions(rows.map((r) => ({ id: r.id, tag: r.tag }))))
        .catch(() => setAssetErr(true));
    }
  }

  function pickAsset(): void {
    const q = assetQuery.trim().toLowerCase();
    const hit = (assetOptions ?? []).find((a) => a.tag.toLowerCase() === q);
    if (!hit) { setAssetErr(true); return; }
    setFocus({ type: "asset", ref: String(hit.id), label: hit.tag });
    setAssetPickOpen(false); setAssetQuery(""); setAssetErr(false);
  }

  // `q`/`tool` come from a starter chip (direct tool dispatch); the input path
  // calls send() with no args, reading from + clearing the input state instead.
  async function send(q?: string, tool?: string): Promise<void> {
    const text = (q ?? input).trim();
    if (!text || busy) return;
    if (q === undefined) setInput("");
    const history: ChatMessage[] = [...turns, { role: "user" as const, content: text }]
      .slice(-HISTORY_TURNS)
      .map((t) => ({ role: t.role, content: t.content }));
    setTurns((prev) => [...prev, { role: "user", content: text, at: Date.now() }]);
    setBusy(true);
    try {
      const res = await agentChat(text, history, focus, tool);
      setTurns((prev) => [...prev, { role: "assistant", content: res.answer, metrics: res.metrics, at: Date.now() }]);
    } catch (e) {
      setTurns((prev) => [...prev, { role: "assistant", content: `Error: ${errMsg(e)}`, at: Date.now() }]);
    } finally {
      setBusy(false);
    }
  }

  // Starter chips removed entirely ("ตรงนี้ไม่ต้องมี เอาออก") — MODS is now a
  // clean input surface. Typed questions still route deterministically via the
  // backend keyword router ("worst CMLs", "ครบกำหนด", "list assets", and
  // readings/trend once a ⬡ focus is set), and the honest fallback suggests
  // those phrasings on a miss.

  // No own header/toggle here: DockWindow supplies the title ("MODS"), icon,
  // minimize (-) and close (x) chrome (see ModsDockMounter). This div just
  // fills the DockWindow body slot (which is itself flex-col + h-bounded).
  // Background set to the shared cream `--paper` tint (same as TeamChatBody's
  // root) — DockWindow's own chrome is white (panel-900), so without this the
  // two dock bodies would visibly differ even after every other token swap.
  return (
    <div className="flex flex-col h-full w-full" style={{ background: "var(--paper)" }}>
      {!aiEntitled ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
          <div className="w-10 h-10 flex items-center justify-center border border-hairline bg-paper-2">
            <Lock size={16} className="text-accent-deep" />
          </div>
          <div className="font-mono text-xs uppercase tracking-wider text-ink-1">MODS is a premium feature</div>
          <p className="font-mono text-[10px] text-ink-3 leading-relaxed">
            Ask plain-language questions about corrosion rate, remaining life and risk across your assets.
          </p>
          <button className="font-mono text-[11px] px-3 py-1.5 rounded-full bg-accent text-[var(--ink-1)]"
                  style={{ fontWeight: 700 }}
                  title="Premium features are enabled by your XZYMODS account manager">
            Upgrade to unlock
          </button>
        </div>
      ) : (
      <>
      {/* AI-disclosure banner removed: MODS's live surface is the deterministic
          chips (no AI runtime in prod), so an "AI-generated" notice over-claimed
          AI involvement. The light-touch caveats remain — the empty-state lead
          line ("ตัวเลขจาก engine ไม่ใช่ AI") and, for customers, the
          "ไม่ใช่คำวินิจฉัย" line. */}
      {focus && focus.ref && (
        <div className="px-3 py-1.5 border-b border-hairline font-mono text-[10px] text-ink-4 flex items-center gap-1">
          <span>Asking about: <span className="text-accent-deep">{focus.label || focus.ref}</span> ({focus.type})</span>
          <button type="button" onClick={() => setFocus(null)} aria-label="ล้างโฟกัส" className="p-0.5 ml-auto shrink-0">
            <X size={11} style={{ color: "var(--ink-4)" }} />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto flex flex-col bg-paper-2">
        <div className="mt-auto px-3 py-3 flex flex-col gap-2.5">
        {turns.length === 0 && !isStaff && (
          /* Customer caveat: MODS for a customer is records-only — the data
             isn't an assessment. The lead line + suggestion chips are gone
             (a clean input surface); this liability line stays. */
          <p className="font-mono text-[10px] text-ink-4 leading-relaxed">
            เป็นข้อมูล ไม่ใช่คำวินิจฉัย — การประเมินสภาพต้องให้วิศวกรผู้ตรวจสรุป
          </p>
        )}
        {turns.map((t, i) => {
          const isUser = t.role === "user";
          // Bubble language mirrors TeamChatDock's EventBubble exactly: a
          // right-aligned accent-tinted bubble for the question, a
          // left-aligned paper-tinted bubble (with a small role label) for
          // the answer — same corner radii, padding and font. The metrics/
          // table card renders INSIDE the answer bubble, restyled to
          // paper-2/hairline so it reads as nested content rather than a
          // second competing surface.
          return (
            <div key={i} className={isUser ? "self-end" : "self-start"} style={{ maxWidth: "82%" }}>
              <div className="font-mono px-2.5 py-1.5" style={{
                fontSize: 12, lineHeight: 1.45,
                background: isUser ? "rgba(var(--accent-rgb) / 0.1)" : "var(--paper)",
                border: isUser ? "none" : "1px solid var(--hairline)",
                color: "var(--ink-1)",
                borderRadius: isUser ? "10px 10px 2px 10px" : "10px 10px 10px 2px",
              }}>
                {!isUser && <div style={{ fontSize: 9.5, color: "var(--accent-deep)", marginBottom: 2 }}>MODS</div>}
                {!isUser && t.metrics && typeof t.metrics === "object"
                  && !Array.isArray(t.metrics) && (
                  // Retrieval agent returns {tool, rows, summary}; the specialist agents
                  // return their own metric shapes. Branch on the retrieval signature.
                  "tool" in t.metrics && "rows" in t.metrics
                    ? <RetrievalCard m={t.metrics as Record<string, unknown>} />
                    : <MetricsCard m={t.metrics as Record<string, unknown>} />
                )}
                <div className="text-xs leading-relaxed">{t.content}</div>
              </div>
              <div className={`font-mono ${isUser ? "text-right" : ""}`} style={{ fontSize: 8.5, color: "var(--ink-4)", marginTop: 2 }}>
                {fmtWhen(t.at)}
              </div>
            </div>
          );
        })}
        {busy && <TypingIndicator />}
        <div ref={bottomRef} />
        </div>
      </div>

      {/* Persistent quick-question strip — kept reachable at all times (not just
          the empty state) so the honest `_fallback_message` ("use the quick-
          question buttons below") stays true even after a turn exists, incl.
          when the last answer was a fallback/no-data response. Focus-dependent
          chips remain gated on focus above. */}
      <div className="border-t px-2.5 py-2" style={{ borderColor: "var(--hairline)" }}>
        {/* Free-text is the honest work-in-progress affordance — it dead-ends
            to the `_fallback_message` in prod (no LLM deployed); the
            placeholder says so. The PREMIUM badge now sits beside the MODS
            window title (registered via the dock spec's `badge`). */}
        {assetPickOpen && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="font-mono" style={{ fontSize: 10, color: "var(--ink-4)", whiteSpace: "nowrap" }}>โฟกัส asset</span>
            <input autoFocus list="mods-asset-options" value={assetQuery} aria-label="แท็ก asset"
                   onChange={(e) => { setAssetQuery(e.target.value); if (assetErr) setAssetErr(false); }}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); pickAsset(); } if (e.key === "Escape") setAssetPickOpen(false); }}
                   placeholder="เช่น V-201" className="flex-1 min-w-0 px-2 py-1 outline-none font-mono"
                   style={{ fontSize: 11, background: "var(--paper-2)", border: "1px solid var(--hairline)", borderRadius: 12, color: "var(--ink-1)" }} />
            <datalist id="mods-asset-options">
              {(assetOptions ?? []).map((a) => <option key={a.id} value={a.tag} />)}
            </datalist>
            <button type="button" onClick={pickAsset} className="font-mono px-2.5 py-1 rounded-full shrink-0"
                    style={{ fontSize: 10, color: "var(--accent-deep)", border: "1px solid var(--accent)" }}>โฟกัส</button>
          </div>
        )}
        {assetErr && (
          <div className="font-mono mb-1" style={{ fontSize: 10, color: "var(--err)" }}>ไม่พบ asset นี้ในระบบ</div>
        )}
        <div className="flex items-center gap-0.5">
          {/* The SHARED TrayToggle/ComposerTray (dock/ComposerTray.tsx) — the
              press-+ effect is identical to the team chat's by construction.
              The tray holds TOOLS only (the suggested questions live in the
              empty state instead). */}
          <TrayToggle open={chipsOpen} onClick={() => setChipsOpen((v) => !v)} />
          <ComposerTray open={chipsOpen} width={40}>
            <button type="button" onClick={openAssetPick} aria-label="เลือก asset"
                    title="โฟกัสคำถามที่ asset (ปลดล็อก readings/แนวโน้ม)"
                    tabIndex={chipsOpen ? 0 : -1} className="p-1 shrink-0">
              <Hexagon size={15} style={{ color: focus && focus.ref ? "var(--accent-deep)" : "var(--ink-3)" }} />
            </button>
          </ComposerTray>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void send(); }}
            placeholder="Ask MODS… (เร็วๆ นี้)"
            className="flex-1 min-w-0 px-3 py-1.5 ml-1 outline-none font-mono"
            style={{ fontSize: 12, background: "var(--paper-2)", border: "1px solid var(--hairline)", borderRadius: 18, color: "var(--ink-1)" }}
          />
          <button onClick={() => void send()} disabled={busy || !input.trim()}
            className="flex items-center justify-center shrink-0 disabled:opacity-40 transition-colors"
            style={{ width: 30, height: 30, borderRadius: 999, background: "var(--accent)", color: "#fff" }}
            aria-label="Send">
            <Send size={15} />
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

// One-shot "open MODS" bridge (workbench shell, 2026-07-24) — the menu bar's
// เครื่องมือ ▸ MODS item lives in AppShell's header, OUTSIDE <DockProvider>
// (same problem TeamChatDock's openChatForTenant solves for the notification
// bell — see that module's docstring). Unlike the chat bridge, MODS is
// ALREADY mounted for every session it applies to (no tenant-select gate to
// wait on), so no reload/marker dance is needed — a plain DOM event the
// already-mounted ModsDockMounter listens for is enough to restore it.
const OPEN_MODS_EVENT = "xzynexa:open-mods";
export function openModsDock(): void {
  window.dispatchEvent(new Event(OPEN_MODS_EVENT));
}

// ModsDockMounter — registers the MODS window with the shared dock. Mounted
// once by AppShell for staff (any route — the old /intake and /submissions
// route exclusion is gone now that windows can coexist instead of
// colliding). Re-registers on every render so DockContext's own idempotent
// bail-out (identical title/icon/body/unread) is what actually decides
// whether anything changes; here title/icon/body are all static so, after
// the first run, this is effectively a no-op on every subsequent render.
export function ModsDockMounter(): JSX.Element | null {
  const { registerWindow, unregisterWindow, restore } = useDock();
  // Same STAFF_ROLES source as ModsBody/AppShell — no second notion of
  // "staff" invented here. Staff keep the old default-open behavior; a
  // customer's default surface is the team chat, so MODS opens closed and
  // appears as a launcher pill they can open beside it (progressive
  // disclosure — spec Phase 2).
  const cachedUser = getCachedUser();
  const isStaff = !!cachedUser && (STAFF_ROLES as readonly string[]).includes(cachedUser.role);
  useEffect(() => {
    registerWindow("mods", {
      title: "MODS",
      icon: <Bot size={14} className="text-brand" />,
      badge: <PremiumBadge light />,
      body: <ModsBody />,
      defaultOpen: isStaff, // matches the old aside's `useState(true)` default for staff
    });
    return () => unregisterWindow("mods");
  }, [registerWindow, unregisterWindow, isStaff]);
  useEffect(() => {
    const onOpen = () => restore("mods");
    window.addEventListener(OPEN_MODS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_MODS_EVENT, onOpen);
  }, [restore]);
  return null;
}
